// SubjectFields — B2: "Who is this request for?" The first section of the requester form and of the
// console's staff fill-in. Two radios with NO default ("Me" · "Someone who lives with me, or will");
// the second reveals "Their first and last name" and "Are they under 18?" (Yes · No, also no default).
// Everything after it re-voices from this choice.
//
// Accessibility (B10): each group is a <fieldset><legend> with a shared radio `name`, and the name is
// an input with a <label htmlFor>.
//
//   cd apps/web && npx tsx --test src/components/rra/SubjectFields.test.ts

import { WHO_FOR } from '@/lib/rraCopy';
import { FIELD_ID, FORM_UI, type SubjectChoice } from '@/lib/rraRequestDraft';

export interface SubjectValue {
  choice: SubjectChoice;
  name: string;
  /** undefined = not answered. */
  minor: boolean | undefined;
}

// Clears the fieldset's own border/padding/side margins only: a block margin of 0 would override
// the design system's `.stack > * + *` spacing and butt the group against the field above it.
const groupReset = { border: 0, padding: 0, marginInline: 0, minWidth: 0 } as const;

export function SubjectFields(props: {
  /** Prefix for the radio `name`s (unique per surface on the page). */
  idPrefix: string;
  value: SubjectValue;
  onChange: (patch: Partial<SubjectValue>) => void;
  disabled?: boolean;
  /** Fields the "Still needed" list named — marked invalid with an inline helper. */
  needed?: ReadonlySet<string>;
  /** The legend of the first question (the staff fill-in uses the same words). */
  heading?: string;
}): React.JSX.Element {
  const { idPrefix, value, disabled = false, onChange } = props;
  const needed = props.needed ?? new Set<string>();
  const nameNeeded = needed.has(FIELD_ID.whoName);
  return (
    <div className="stack" style={{ gap: 'var(--space-3)' }}>
      <fieldset id={FIELD_ID.who} style={groupReset} aria-describedby={needed.has(FIELD_ID.who) ? `${FIELD_ID.who}-needed` : undefined}>
        <legend style={{ fontWeight: 'var(--weight-medium)', padding: 0, marginBottom: 'var(--space-2)' }}>
          {props.heading ?? WHO_FOR.heading}
        </legend>
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <label className="choice" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="radio"
              name={`${idPrefix}-who`}
              checked={value.choice === 'me'}
              disabled={disabled}
              onChange={() => onChange({ choice: 'me' })}
              style={{ flex: 'none', marginTop: '0.2em' }}
            />
            <span>{WHO_FOR.me}</span>
          </label>
          <label className="choice" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="radio"
              name={`${idPrefix}-who`}
              checked={value.choice === 'someone'}
              disabled={disabled}
              onChange={() => onChange({ choice: 'someone' })}
              style={{ flex: 'none', marginTop: '0.2em' }}
            />
            <span>{WHO_FOR.someone}</span>
          </label>
        </div>
        {needed.has(FIELD_ID.who) ? (
          <p id={`${FIELD_ID.who}-needed`} role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', margin: 'var(--space-2) 0 0' }}>
            {FORM_UI.fieldNeeded}
          </p>
        ) : null}
      </fieldset>

      {value.choice === 'someone' ? (
        <>
          <div className="field">
            <label htmlFor={FIELD_ID.whoName}>{WHO_FOR.nameLabel}</label>
            <input
              id={FIELD_ID.whoName}
              className="input"
              maxLength={200}
              value={value.name}
              disabled={disabled}
              {...(nameNeeded ? { 'aria-invalid': 'true' as const } : {})}
              onChange={(e) => onChange({ name: e.target.value })}
            />
            {nameNeeded ? (
              <span className="helper" data-state="error" role="alert">
                {FORM_UI.fieldNeeded}
              </span>
            ) : null}
          </div>
          <fieldset id={FIELD_ID.whoMinor} style={groupReset}>
            <legend style={{ fontWeight: 'var(--weight-medium)', padding: 0, marginBottom: 'var(--space-2)' }}>
              {WHO_FOR.minorLabel}
            </legend>
            <div className="cluster" style={{ gap: 'var(--space-5)' }}>
              <label className="choice" style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={`${idPrefix}-minor`}
                  checked={value.minor === true}
                  disabled={disabled}
                  onChange={() => onChange({ minor: true })}
                />{' '}
                {WHO_FOR.yes}
              </label>
              <label className="choice" style={{ cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={`${idPrefix}-minor`}
                  checked={value.minor === false}
                  disabled={disabled}
                  onChange={() => onChange({ minor: false })}
                />{' '}
                {WHO_FOR.no}
              </label>
            </div>
            {needed.has(FIELD_ID.whoMinor) ? (
              <p role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)', margin: 'var(--space-2) 0 0' }}>
                {FORM_UI.fieldNeeded}
              </p>
            ) : null}
          </fieldset>
        </>
      ) : null}
    </div>
  );
}
